/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import StripeSync from './StripeSync';
import en from '../../assets/json/en.json';
import zh from '../../assets/json/zh.json';
import cn from '../../assets/json/cn.json';
export const stripeText = language => {
  const locale = { en, zh, cn }[language] || en;
  return (locale.ACCOUNTOVERVIEW || locale.AccountOverview).stripeReconciliation;
};
const money = (value, currency) => value == null ? '—' : new Intl.NumberFormat('en-HK', {
  style: 'currency', currency: (currency || 'HKD').toUpperCase()
}).format(value);
export const stripeGross = payment => payment.stripeChargeMode === 'direct' && Number.isSafeInteger(payment.stripeExpectedGross)
  ? payment.stripeExpectedGross / 100 : Number(payment.baseAmount ?? payment.amount);
export const isStripeVerified = payment => payment.stripeFeeStatus === 'confirmed' &&
  !payment.stripePaymentReviewAt && !payment.stripeSyncExhausted && !payment.receiptStale && !payment.stripeManualReview?.changes?.length &&
  ['sent', 'manual'].includes(payment.stripeReceiptDelivery);
const StripeReconciliation = forwardRef(function StripeReconciliation({ payment, language = 'en', onRefresh = () => {}, showSyncButton = true, showVerifiedLabel = true, onCheckingChange }, ref) {
  const canSync = payment.PaymentMode === 'Stripe' && payment.status === 'complete' && payment.id != null;
  if (!payment.stripeFeeStatus && !canSync) return null;
  const text = stripeText(language);
  const confirmed = payment.stripeFeeStatus === 'confirmed';
  const differences = !!payment.stripeManualReview?.changes?.length;
  const review = differences || payment.stripePaymentReviewAt || payment.stripeSyncExhausted || payment.stripeFeeStatus === 'review' || payment.stripeReceiptDelivery === 'review';
  const donationOnly = !payment.stripePaymentReviewAt && payment.stripeReceiptBasis === 'donation' && payment.coverFee === false && payment.stripePaymentVerifiedAt;
  const receiptSent = payment.stripeReceiptDelivery === 'sent';
  const manuallyVerified = confirmed && payment.stripeManualCheckedAt && !review;
  const compact = isStripeVerified(payment);
  let body = review ? text.stopped : !confirmed ? text.waiting :
    receiptSent ? text.sent : payment.stripeReceiptDelivery === 'manual' ? text.manual : text.preparing;
  if (donationOnly && !confirmed) {
    if (receiptSent) body = review ? text.receiptSentFeeReview : text.receiptSentFeesPending;
    else if (payment.stripeReceiptDelivery === 'manual') body = review ? text.manualFeesReview : text.manualFeesPending;
    else if (!review) body = text.preparing;
  }
  if (donationOnly && payment.stripeReceiptDelivery === 'review') body = text.donationReceiptReview;
  if (differences) body = text.savedDifferences;
  return <section className={`AccountOverview-stripeVerification${compact ? ' AccountOverview-stripeVerification--compact' : ''}`} aria-label={text.title}>
    {compact ? showVerifiedLabel && <div className="AccountOverview-stripeCompactLabel"><span aria-hidden="true">✓</span>{text.verifiedLabel}</div> : <React.Fragment>
      <div className="AccountOverview-stripeHeading">
        <h2>{text.title}</h2>
        <span className={`AccountOverview-stripeBadge ${review ? 'is-review' : confirmed ? 'is-confirmed' : 'is-pending'}`}>
          {manuallyVerified ? <span aria-hidden="true">✓</span> : <span aria-hidden="true" className="AccountOverview-stripeStatusDot" />}
          {differences ? text.differencesLabel : manuallyVerified ? text.verifiedLabel : !payment.stripeFeeStatus ? text.historical : review ? text.review : confirmed ? text.confirmed : text.pending}
        </span>
      </div>
      <p className="AccountOverview-stripeDescription">{!payment.stripeFeeStatus ? text.historicalHelp : body}</p>
      <div className="AccountOverview-stripeSummary">
        {['fee', 'net'].map(key => <div className="AccountOverview-stripeMetric" key={key}>
          <span>{text[key]}{!confirmed && <small>{text.estimated}</small>}</span>
          <strong>{money(payment[key], payment.currency)}</strong>
        </div>)}
      </div>
    </React.Fragment>}
    {canSync && <StripeSync key={payment.id} ref={ref} payment={payment} text={text} onRefresh={onRefresh} showSyncButton={showSyncButton} compact={compact} onCheckingChange={onCheckingChange} />}
    {!review && !confirmed && payment.stripeSyncNextAt && Number.isFinite(Date.parse(payment.stripeSyncNextAt)) &&
      <p>{text.nextCheck}: {new Date(payment.stripeSyncNextAt).toLocaleString()}</p>}
  </section>;
});
export default StripeReconciliation;
export function StripeSettlementRows({ payment, language = 'en' }) {
  const text = stripeText(language);
  const estimated = payment.stripeFeeStatus !== 'confirmed';
  return <React.Fragment>
    {['processingFee', 'serviceFee', 'fee', 'net'].map((key, index) => <tr key={key} className="AccountOverview-paymentRow">
      <th><div className="AccountOverview-description">{[text.processing, text.service, text.fee, text.net][index]}{estimated ? ` (${text.estimated})` : ''}</div></th>
      <th><div className="AccountOverview-dataInput">{money(payment[key], payment.currency)}</div></th>
    </tr>)}
    {payment.stripePlatformCostAdjustment !== 0 && payment.stripePlatformCostAdjustment != null && <tr className="AccountOverview-paymentRow">
      <th><div className="AccountOverview-description">{text.adjustment}<p>{text.adjustmentHelp}</p></div></th>
      <th><div className="AccountOverview-dataInput">{money(payment.stripePlatformCostAdjustment, payment.currency)}</div></th>
    </tr>}
  </React.Fragment>;
}
StripeReconciliation.propTypes = StripeSettlementRows.propTypes = {
  payment: PropTypes.object.isRequired, language: PropTypes.string, onRefresh: PropTypes.func, showSyncButton: PropTypes.bool, showVerifiedLabel: PropTypes.bool, onCheckingChange: PropTypes.func
};
